/**
 * Site Reliability API Client
 *
 * Adapter for GET {apiHost}/admin-api/site/{siteId}/reliability. Returns the domain
 * model as a discriminated union so expected outcomes (404, HTTP errors) are values,
 * not exceptions. Errors are not sent to analytics because this endpoint is polled.
 */
import { toSiteReliability } from '../models/reliabilityAdapter';
import type { SiteReliability, SiteReliabilityResponse } from '../models/reliabilityModels';

export interface ReliabilityApiConfig {
  readonly apiHost: string;
  readonly getAccessToken: () => Promise<string | null>;
}

export type ReliabilityResult =
  | { readonly status: 'ok'; readonly data: SiteReliability }
  | { readonly status: 'not_found' }
  | { readonly status: 'error'; readonly message: string };

/**
 * @throws Error when no access token is available, or on network failure
 */
export async function fetchSiteReliability(
  siteId: string,
  config: ReliabilityApiConfig
): Promise<ReliabilityResult> {
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(
    `${config.apiHost}/admin-api/site/${encodeURIComponent(siteId)}/reliability`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 404) {
    return { status: 'not_found' };
  }

  if (!response.ok) {
    return { status: 'error', message: `API error: ${response.status}` };
  }

  const body: SiteReliabilityResponse = await response.json();
  return { status: 'ok', data: toSiteReliability(body) };
}
